/**
 * Shared mesh helpers for import/export codecs. Pure functions — usable from
 * the io worker, the kernel, and vitest alike.
 */

import type { MeshData } from '../geometry/types';

/** Non-indexed triangle soup → MeshData with flat per-face normals. */
export function soupToMeshData(positions: Float32Array): MeshData {
  const normals = new Float32Array(positions.length);
  const indices = new Uint32Array(positions.length / 3);
  for (let t = 0; t < positions.length; t += 9) {
    const ax = positions[t]!, ay = positions[t + 1]!, az = positions[t + 2]!;
    const bx = positions[t + 3]!, by = positions[t + 4]!, bz = positions[t + 5]!;
    const cx = positions[t + 6]!, cy = positions[t + 7]!, cz = positions[t + 8]!;
    let nx = (by - ay) * (cz - az) - (bz - az) * (cy - ay);
    let ny = (bz - az) * (cx - ax) - (bx - ax) * (cz - az);
    let nz = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    for (let k = 0; k < 3; k++) {
      normals[t + k * 3] = nx;
      normals[t + k * 3 + 1] = ny;
      normals[t + k * 3 + 2] = nz;
    }
  }
  for (let i = 0; i < indices.length; i++) indices[i] = i;
  return { positions, normals, indices, edgePositions: new Float32Array(0) };
}

export interface MeshTransform {
  position: readonly [number, number, number];
  rotationEuler: readonly [number, number, number];
  scale: readonly [number, number, number];
}

/** Bake T·R·S into a copy of the mesh (XYZ euler order, radians). */
export function bakeTransform(mesh: MeshData, t: MeshTransform): MeshData {
  const [rx, ry, rz] = t.rotationEuler;
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const cy = Math.cos(ry), sy = Math.sin(ry);
  const cz = Math.cos(rz), sz = Math.sin(rz);
  // R = Rz * Ry * Rx
  const r00 = cz * cy, r01 = cz * sy * sx - sz * cx, r02 = cz * sy * cx + sz * sx;
  const r10 = sz * cy, r11 = sz * sy * sx + cz * cx, r12 = sz * sy * cx - cz * sx;
  const r20 = -sy, r21 = cy * sx, r22 = cy * cx;

  const positions = new Float32Array(mesh.positions.length);
  const normals = new Float32Array(mesh.normals.length);
  const [sxv, syv, szv] = t.scale;
  const [px, py, pz] = t.position;
  for (let i = 0; i < mesh.positions.length; i += 3) {
    const x = mesh.positions[i]! * sxv;
    const y = mesh.positions[i + 1]! * syv;
    const z = mesh.positions[i + 2]! * szv;
    positions[i] = r00 * x + r01 * y + r02 * z + px;
    positions[i + 1] = r10 * x + r11 * y + r12 * z + py;
    positions[i + 2] = r20 * x + r21 * y + r22 * z + pz;
    const nx = mesh.normals[i]!, ny = mesh.normals[i + 1]!, nz = mesh.normals[i + 2]!;
    normals[i] = r00 * nx + r01 * ny + r02 * nz;
    normals[i + 1] = r10 * nx + r11 * ny + r12 * nz;
    normals[i + 2] = r20 * nx + r21 * ny + r22 * nz;
  }
  const edgePositions = new Float32Array(mesh.edgePositions.length);
  for (let i = 0; i < mesh.edgePositions.length; i += 3) {
    const x = mesh.edgePositions[i]! * sxv;
    const y = mesh.edgePositions[i + 1]! * syv;
    const z = mesh.edgePositions[i + 2]! * szv;
    edgePositions[i] = r00 * x + r01 * y + r02 * z + px;
    edgePositions[i + 1] = r10 * x + r11 * y + r12 * z + py;
    edgePositions[i + 2] = r20 * x + r21 * y + r22 * z + pz;
  }
  return { positions, normals, indices: mesh.indices.slice(), edgePositions };
}

/** FNV-1a over bytes → hex string; content addressing for the asset store. */
export function hashBytes(bytes: Uint8Array): string {
  let a = 0x811c9dc5;
  let b = 0x01000193;
  for (let i = 0; i < bytes.length; i++) {
    a = ((a ^ bytes[i]!) * 16777619) >>> 0;
    b = ((b ^ bytes[i]!) * 16777619 + 1) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}
